import { createClient } from "@supabase/supabase-js";

const supabaseUrl = "https://gbiaopuamdekvasouekf.supabase.co";
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper functions for content management
export const contentService = {
  // Get content by section key
  getContent: async (sectionKey, page = "home") => {
    const { data, error } = await supabase
      .from("content_sections")
      .select("*")
      .eq("section_key", sectionKey)
      .eq("page", page)
      .single();

    if (error && error.code !== "PGRST116") {
      console.error("Error fetching content:", error);
      return null;
    }

    return data;
  },

  // Update or create content
  upsertContent: async (
    sectionKey,
    content,
    page = "home",
    contentType = "text"
  ) => {
    const { data, error } = await supabase.from("content_sections").upsert(
      {
        section_key: sectionKey,
        content,
        page,
        content_type: contentType,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: "section_key,page",
      }
    );

    if (error) {
      console.error("Error updating content:", error);
      throw error;
    }

    return data;
  },

  // Get all services
  getServices: async () => {
    const { data, error } = await supabase
      .from("services")
      .select("*")
      .eq("is_active", true)
      .order("order_index", { ascending: true });

    if (error) {
      console.error("Error fetching services:", error);
      return [];
    }

    return data || [];
  },

  // Get team members
  getTeamMembers: async () => {
    const { data, error } = await supabase
      .from("team_members")
      .select("*")
      .order("order_index", { ascending: true });

    if (error) {
      console.error("Error fetching team members:", error);
      return [];
    }

    return data || [];
  },

  // Get FAQ items
  getFAQItems: async (category = null) => {
    let query = supabase
      .from("faq_items")
      .select("*")
      .eq("is_active", true)
      .order("order_index", { ascending: true });

    if (category) {
      query = query.eq("category", category);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching FAQ items:", error);
      return [];
    }

    return data || [];
  },

  // Upload image
  uploadImage: async (file, bucket = "website-images") => {
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2)}.${fileExt}`;

      const { error } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error("Error uploading image:", error);
        console.error("Error details:", {
          message: error.message,
          statusCode: error.statusCode,
          error: error.error
        });
        throw error;
      }

      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error("Upload function failed:", error);
      throw error;
    }
  },
};

// Utility function to get image URL from Supabase storage
export const getImageUrl = (fileName) => {
  if (!fileName) return '';
  
  const { data } = supabase.storage
    .from('website-images')
    .getPublicUrl(fileName);
    
  return data.publicUrl;
};

// Booking service functions
export const bookingService = {
  // Create a new booking
  createBooking: async (bookingData) => {
    const { data, error } = await supabase
      .from("bookings")
      .insert([
        {
          first_name: bookingData.firstName,
          last_name: bookingData.lastName,
          email: bookingData.email,
          phone: bookingData.phone,
          location_id: bookingData.locationId,
          doctor_id: bookingData.doctorId,
          preferred_date: bookingData.preferredDate,
          preferred_time: bookingData.preferredTime,
          symptoms: bookingData.symptoms,
        }
      ])
      .select(`
        *,
        location:locations(title, address, phone),
        doctor:team_members(name, title, specialization)
      `)
      .single();

    if (error) {
      console.error("Error creating booking:", error);
      throw error;
    }

    return data;
  },

  // Get all bookings for admin
  getAllBookings: async (status = null) => {
    let query = supabase
      .from("booking_details")
      .select("*")
      .order("created_at", { ascending: false });

    if (status) {
      query = query.eq("status", status);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching bookings:", error);
      return [];
    }

    return data || [];
  },

  // Update booking status
  updateBookingStatus: async (bookingId, status, adminNotes = null) => {
    const updateData = { status };
    if (adminNotes) {
      updateData.admin_notes = adminNotes;
    }

    const { data, error } = await supabase
      .from("bookings")
      .update(updateData)
      .eq("id", bookingId)
      .select()
      .single();

    if (error) {
      console.error("Error updating booking:", error);
      throw error;
    }

    return data;
  },

  // Get booking statistics
  getBookingStats: async () => {
    const { data, error } = await supabase
      .from("bookings")
      .select("status, preferred_date, created_at");

    if (error) {
      console.error("Error fetching booking stats:", error);
      return null;
    }

    const stats = {
      total: data.length,
      pending: data.filter(b => b.status === 'pending').length,
      confirmed: data.filter(b => b.status === 'confirmed').length,
      completed: data.filter(b => b.status === 'completed').length,
      cancelled: data.filter(b => b.status === 'cancelled').length,
      thisWeek: data.filter(b => {
        const bookingDate = new Date(b.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return bookingDate >= weekAgo;
      }).length
    };

    return stats;
  },

  // Send booking confirmation email
  sendBookingEmail: async (bookingData) => {
    try {
      // Go back to the original working method  
      const { data, error } = await supabase.functions.invoke('send-booking-email', {
        body: { bookingData }
      });

      if (error) {
        console.error("Error sending email:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Email service error:", error);
      throw error;
    }
  }
};

// Image mappings for easy access
export const imageUrls = {
  // Main images
  spirometrie: () => getImageUrl('spirometrie.jpeg'),
  consult: () => getImageUrl('consult.jpeg'),
  pulsoximetrie: () => getImageUrl('pulsoximetrie.jpeg'),
  apnee: () => getImageUrl('apnee.webp'),
  fisaAuto: () => getImageUrl('fisa-auto.jpeg'),
  masaj: () => getImageUrl('masaj.jpeg'),
  mainHeaderLogo: () => getImageUrl('mainheader-logo.webp'),
  pricesSection: () => getImageUrl('prices-img-section.jpeg'),
  cabinet1: () => getImageUrl('cabinet1.webp'),
  cabinet2: () => getImageUrl('cabinet2.webp'),
  findUs: () => getImageUrl('findus.avif'),
  
  // Team members
  drAli: () => getImageUrl('drali.webp'),
  drDruga: () => getImageUrl('drdruga.jpeg'),
  drPopescu: () => getImageUrl('popescu.webp'),
  drBranzei: () => getImageUrl('branzei.webp'),
  
  // Testimonials
  person1: () => getImageUrl('person1.png'),
  person2: () => getImageUrl('person2.webp'),
  person3: () => getImageUrl('person3.png'),
  
  // Logos
  logo: () => getImageUrl('logo.png'),
  tbcLogo: () => getImageUrl('tbc-logo.jpeg'),
  
  // Price services (with different names to avoid conflicts)
  spirometriePrice: () => getImageUrl('spirometrie-price.jpeg'),
  ekg: () => getImageUrl('ekg.jpeg'),
  pulsoximetriePrice: () => getImageUrl('pulsoximetrie-price.jpeg'),
  consultPneumologic: () => getImageUrl('consult-pneumologic.jpeg'),
  consultApnee: () => getImageUrl('consult-apnee.jpeg'),
  fisaAutoPrice: () => getImageUrl('fisa-auto-price.jpeg'),
  adeverinta: () => getImageUrl('adeverinta.jpeg'),
  terapiiComplementare: () => getImageUrl('terapii-complementare.jpeg'),
  testareInjectie: () => getImageUrl('testare-injectie.jpeg'),
  antiAging: () => getImageUrl('anti-aging.webp'),
  cognitiva: () => getImageUrl('cognitiva.jpeg'),
  handicap: () => getImageUrl('handicap.jpeg'),
  handicapPng: () => getImageUrl('handicap.png'),
  intravenos: () => getImageUrl('intravenos.jpeg'),
};
